// Per-deck members dialog. Drives the PR #16 + #19 endpoints:
//   GET    /api/decks/:id/members
//   POST   /api/decks/:id/members
//   PATCH  /api/decks/:id/members/:user_id
//   DELETE /api/decks/:id/members/:user_id
//   POST   /api/decks/:id/members/:user_id/transfer-owner
//
// Slice 10 also wires per-deck agent CRUD:
//   GET    /api/decks/:id/agents
//   POST   /api/decks/:id/agents/:slug
//   DELETE /api/decks/:id/agents/:slug
//
// Agent membership is now a real roster (deck_agents table; Slice 8)
// rather than the legacy single decks.tulpa_id pointer, so users can
// bind multiple personas to one deck.

import React, { useCallback, useEffect, useState } from 'react';
import { X, UserPlus, SpinnerGap, Trash, Crown } from '@phosphor-icons/react';
import {
  addDeckAgent,
  inviteDeckMember,
  listDeckMembers,
  listPpTulpas,
  removeDeckAgent,
  removeDeckMember,
  setDeckMemberRole,
  transferDeckOwner,
  type DeckMember,
  type PpTulpa,
} from '../api/client';

const overlayStyle: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'var(--ds-accent-soft)', zIndex: 1000,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
};
const dialogStyle: React.CSSProperties = {
  background: 'var(--bg-primary, #fff)', borderRadius: 12,
  width: 'min(560px, 92vw)', boxShadow: '0 16px 48px var(--ds-shadow-color)',
  padding: 24, fontFamily: 'inherit', color: 'var(--text-primary, #0F172A)',
  maxHeight: '88vh', overflow: 'auto',
};
const btnStyle: React.CSSProperties = {
  padding: '6px 10px', border: '1px solid var(--border, #e2e8f0)',
  borderRadius: 6, background: 'var(--bg-surface, #fff)',
  cursor: 'pointer', fontSize: 12, color: 'var(--text-primary, #0F172A)',
  fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 4,
};
const ghostBtnStyle: React.CSSProperties = { ...btnStyle, border: 'none', background: 'transparent' };
const primaryBtnStyle: React.CSSProperties = {
  ...btnStyle, background: 'var(--ds-accent)', color: 'var(--ds-on-accent)', borderColor: 'var(--ds-accent)',
  fontWeight: 600,
};
const dangerBtnStyle: React.CSSProperties = {
  ...ghostBtnStyle, color: 'var(--ds-danger)',
};
const inputStyle: React.CSSProperties = {
  flex: 1, padding: '8px 12px', border: '1px solid var(--border, #e2e8f0)',
  borderRadius: 8, fontSize: 14, fontFamily: 'inherit',
  background: 'var(--bg-surface, #fff)', color: 'var(--text-primary, #0F172A)',
  boxSizing: 'border-box', outline: 'none',
};
const selectStyle: React.CSSProperties = {
  ...inputStyle, flex: 'none', width: 90, padding: '4px 6px', fontSize: 12,
};

type Role = 'owner' | 'editor' | 'viewer';

export default function MembersDialog({
  deckId,
  workspaceId,
  currentUserId,
  onClose,
}: {
  deckId: string;
  workspaceId: string | null | undefined;
  currentUserId: string | null | undefined;
  onClose: () => void;
}) {
  const [members, setMembers] = useState<DeckMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [invitingBusy, setInvitingBusy] = useState(false);
  const [email, setEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<Role>('editor');
  const [availableTulpas, setAvailableTulpas] = useState<PpTulpa[]>([]);
  const [addAgentSlug, setAddAgentSlug] = useState<string>('');
  const [agentBusy, setAgentBusy] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await listDeckMembers(deckId);
      setMembers(rows);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [deckId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Load the workspace's tulpa registry once so the "add agent"
  // dropdown can show personas the user is allowed to bind. The
  // endpoint already merges system defaults with workspace-local
  // tulpas.
  useEffect(() => {
    if (!workspaceId) return;
    listPpTulpas(workspaceId)
      .then(setAvailableTulpas)
      .catch(() => setAvailableTulpas([]));
  }, [workspaceId]);

  const humans = members.filter((m) => m.member_type === 'human');
  const agents = members.filter((m) => m.member_type === 'agent');
  const me = humans.find((m) => m.member_id === currentUserId);
  const myRole = (me?.role as Role | undefined) ?? null;
  const canInvite = myRole === 'owner' || myRole === 'editor';
  const canChangeRoles = myRole === 'owner';
  const canTransfer = myRole === 'owner';

  const handleAddAgent = async () => {
    if (!addAgentSlug.trim()) {
      setError('Pick a tulpa to add.');
      return;
    }
    setAgentBusy(true);
    setError(null);
    try {
      await addDeckAgent(deckId, addAgentSlug.trim());
      setAddAgentSlug('');
      await refresh();
    } catch (e) {
      setError(`Add agent failed: ${(e as Error).message}`);
    } finally {
      setAgentBusy(false);
    }
  };

  const handleRemoveAgent = async (slug: string) => {
    setAgentBusy(true);
    setError(null);
    try {
      await removeDeckAgent(deckId, slug);
      await refresh();
    } catch (e) {
      setError(`Remove agent failed: ${(e as Error).message}`);
    } finally {
      setAgentBusy(false);
    }
  };

  const handleInvite = async () => {
    const trimmed = email.trim();
    if (!trimmed) {
      setError('Email is required.');
      return;
    }
    setInvitingBusy(true);
    setError(null);
    try {
      await inviteDeckMember(deckId, { email: trimmed, role: inviteRole });
      setEmail('');
      await refresh();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setInvitingBusy(false);
    }
  };

  const handleRoleChange = async (userId: string, role: Role) => {
    setError(null);
    try {
      await setDeckMemberRole(deckId, userId, role);
      await refresh();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const handleRemove = async (userId: string, label: string) => {
    if (!confirm(`Remove ${label} from this deck?`)) return;
    setError(null);
    try {
      await removeDeckMember(deckId, userId);
      await refresh();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const handleTransfer = async (userId: string, label: string) => {
    if (!confirm(`Transfer deck ownership to ${label}? You'll keep editor access.`)) return;
    setError(null);
    try {
      await transferDeckOwner(deckId, userId);
      await refresh();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={dialogStyle} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
            <UserPlus size={18} weight="duotone" /> Deck members
          </h2>
          <button onClick={onClose} style={{ ...btnStyle, padding: 6 }} aria-label="Close">
            <X size={14} />
          </button>
        </div>

        {error && (
          <div style={{ background: 'var(--ds-danger-soft)', color: 'var(--ds-danger)', border: '1px solid var(--ds-danger-soft)', borderRadius: 8, padding: 10, marginBottom: 14, fontSize: 13 }}>
            {error}
          </div>
        )}

        {/* Invite row */}
        {canInvite && (
          <div style={{ display: 'flex', gap: 8, marginBottom: 18 }}>
            <input
              type="email"
              placeholder="someone@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={inputStyle}
              disabled={invitingBusy}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void handleInvite();
                }
              }}
            />
            <select
              value={inviteRole}
              onChange={(e) => setInviteRole(e.target.value as Role)}
              style={selectStyle}
              disabled={invitingBusy || !canChangeRoles && inviteRole === 'owner'}
            >
              <option value="editor">Editor</option>
              <option value="viewer">Viewer</option>
              {canChangeRoles && <option value="owner">Owner</option>}
            </select>
            <button
              onClick={() => void handleInvite()}
              disabled={invitingBusy || !email.trim()}
              style={{ ...primaryBtnStyle, opacity: invitingBusy || !email.trim() ? 0.5 : 1 }}
            >
              {invitingBusy ? <SpinnerGap size={14} /> : 'Invite'}
            </button>
          </div>
        )}

        {loading && (
          <div style={{ color: 'var(--text-muted, #64748b)', fontSize: 13, marginBottom: 12 }}>
            <SpinnerGap size={14} /> Loading members…
          </div>
        )}

        {/* Humans list */}
        <div style={{ marginBottom: 18 }}>
          <div style={{ fontSize: 11, color: 'var(--text-muted, #64748b)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
            People ({humans.length})
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {humans.map((m) => {
              const label = m.display_name || m.email || m.member_id;
              const isSelf = m.member_id === currentUserId;
              return (
                <div
                  key={m.member_id}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', border: '1px solid var(--border, #e2e8f0)', borderRadius: 8 }}
                >
                  <div style={{
                    width: 28, height: 28, borderRadius: '50%',
                    background: m.avatar_url ? `url(${m.avatar_url}) center/cover` : 'var(--ds-accent-soft)',
                    color: 'var(--ds-accent)', fontSize: 12, fontWeight: 600,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    {!m.avatar_url && (label?.[0]?.toUpperCase() ?? '?')}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {label}
                      {isSelf && <span style={{ marginLeft: 6, fontSize: 11, color: 'var(--text-muted, #64748b)' }}>(you)</span>}
                    </div>
                    {m.email && m.email !== label && (
                      <div style={{ fontSize: 11, color: 'var(--text-muted, #64748b)' }}>{m.email}</div>
                    )}
                  </div>
                  {canChangeRoles && !isSelf && m.role !== 'owner' ? (
                    <select
                      value={m.role}
                      onChange={(e) => void handleRoleChange(m.member_id, e.target.value as Role)}
                      style={selectStyle}
                    >
                      <option value="editor">Editor</option>
                      <option value="viewer">Viewer</option>
                    </select>
                  ) : (
                    <span style={{ fontSize: 11, color: 'var(--text-muted, #64748b)', padding: '2px 8px', border: '1px solid var(--border, #e2e8f0)', borderRadius: 4, textTransform: 'capitalize' }}>
                      {m.role}
                    </span>
                  )}
                  {canTransfer && m.role !== 'owner' && (
                    <button
                      onClick={() => void handleTransfer(m.member_id, label)}
                      title="Transfer ownership"
                      style={ghostBtnStyle}
                    >
                      <Crown size={14} weight="light" />
                    </button>
                  )}
                  {canInvite && !isSelf && m.role !== 'owner' && (
                    <button
                      onClick={() => void handleRemove(m.member_id, label)}
                      title="Remove from deck"
                      style={dangerBtnStyle}
                    >
                      <Trash size={14} weight="light" />
                    </button>
                  )}
                </div>
              );
            })}
            {!loading && humans.length === 0 && (
              <div style={{ fontSize: 12, color: 'var(--text-muted, #64748b)', padding: 12, textAlign: 'center' }}>
                No people on this deck yet.
              </div>
            )}
          </div>
        </div>

        {/* Agents — per-deck roster (deck_agents). Owners + editors
            can bind/unbind any tulpa the workspace can see (system
            defaults + workspace-local). */}
        <div>
          <div style={{ fontSize: 11, color: 'var(--text-muted, #64748b)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
            Agents ({agents.length})
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {agents.map((a) => {
              const slug = a.tulpa_id || a.member_id;
              const tulpa = availableTulpas.find((t) => t.slug === slug);
              return (
                <div
                  key={a.member_id}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', border: '1px solid var(--border, #e2e8f0)', borderRadius: 8 }}
                >
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--ds-success)' }} />
                  <div style={{ flex: 1, fontSize: 13 }}>
                    <strong>{tulpa?.name ?? slug}</strong>
                    {tulpa?.description && (
                      <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--text-muted, #64748b)' }}>
                        {tulpa.description}
                      </span>
                    )}
                  </div>
                  {canInvite && (
                    <button
                      style={dangerBtnStyle}
                      disabled={agentBusy}
                      onClick={() => handleRemoveAgent(slug)}
                      title="Remove agent"
                      type="button"
                    >
                      <Trash size={12} weight="light" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
          {canInvite && availableTulpas.length > 0 && (
            <div style={{ display: 'flex', gap: 6, marginTop: 8, alignItems: 'center' }}>
              <select
                value={addAgentSlug}
                onChange={(e) => setAddAgentSlug(e.target.value)}
                disabled={agentBusy}
                style={{ ...inputStyle, flex: 1, padding: '8px 10px' }}
              >
                <option value="">Pick a tulpa to bind…</option>
                {availableTulpas
                  .filter((t) => !agents.some((a) => (a.tulpa_id || a.member_id) === t.slug))
                  .map((t) => (
                    <option key={t.slug} value={t.slug}>
                      {t.name} — {t.slug}
                    </option>
                  ))}
              </select>
              <button
                style={primaryBtnStyle}
                onClick={handleAddAgent}
                disabled={agentBusy || !addAgentSlug}
                type="button"
              >
                <UserPlus size={12} weight="light" />
                Bind
              </button>
            </div>
          )}
        </div>

        <div style={{ fontSize: 11, color: 'var(--text-muted, #64748b)', marginTop: 16, lineHeight: 1.5 }}>
          Invitees must already have a PitchPilot account. Viewers can read but not edit. Owners can transfer ownership; doing so demotes them to editor.
        </div>
      </div>
    </div>
  );
}
